// Deterministic physical sheet geometry. Millimetres are the only source of
// truth; CSS pixels are used by the preview only as a display conversion.

export const PAPER_SIZES = Object.freeze({
  A4: Object.freeze({ id: "a4", name: "A4", orientation: "portrait", widthMM: 210, heightMM: 297 }),
  CUSTOM: Object.freeze({ id: "custom", name: "Custom", widthMM: 210, heightMM: 297 }),
});
export const A4_PAPER = PAPER_SIZES.A4;

const LEGACY_DEFAULT = Object.freeze({
  columns: 3, rows: 7, safePadXMM: 3, safePadYMM: 3,
  labelRowGapMM: 1.5, labelColumnGapMM: 2,
});

export const DEFAULT_SHEET_GEOMETRY = Object.freeze({
  paperSize: "a4",
  orientation: "portrait",
  paperWidthMM: 210,
  paperHeightMM: 297,
  columns: 3,
  rows: 7,
  labelWidthMM: (210 - 6 - 4) / 3,
  labelHeightMM: (297 - 6 - 9) / 7,
  marginTopMM: 3,
  marginRightMM: 3,
  marginBottomMM: 3,
  marginLeftMM: 3,
  safePadXMM: 3,
  safePadYMM: 3,
  labelRowGapMM: 1.5,
  labelColumnGapMM: 2,
  paddingTopMM: 2.2,
  paddingRightMM: 2.2,
  paddingBottomMM: 2.2,
  paddingLeftMM: 2.2,
});

export const DEFAULT_DEVICE_CALIBRATION = Object.freeze({ printOffsetXMM: 0, printOffsetYMM: 0 });
export const SHEET_LIMITS = Object.freeze({
  columns: Object.freeze({ min: 1, max: 8 }),
  rows: Object.freeze({ min: 1, max: 12 }),
});

const toFinite = (value, fallback) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};
const toClampedInteger = (value, fallback, { min, max }) => {
  const numeric = Math.round(Number(value));
  if (!Number.isFinite(numeric)) return fallback;
  return Math.min(max, Math.max(min, numeric));
};
const has = (object, key) => Object.prototype.hasOwnProperty.call(object || {}, key);

const resolvePaper = (geometry) => {
  const paperSize = geometry?.paperSize === "custom" ? "custom" : "a4";
  const orientation = geometry?.orientation === "landscape" ? "landscape" : "portrait";
  const baseWidth = paperSize === "custom" ? toFinite(geometry?.paperWidthMM, 210) : 210;
  const baseHeight = paperSize === "custom" ? toFinite(geometry?.paperHeightMM, 297) : 297;
  const short = Math.min(baseWidth, baseHeight);
  const long = Math.max(baseWidth, baseHeight);
  if (paperSize === "a4" && orientation === "portrait") return A4_PAPER;
  return {
    id: paperSize,
    name: paperSize === "a4" ? "A4" : "Custom",
    orientation,
    widthMM: orientation === "landscape" ? long : short,
    heightMM: orientation === "landscape" ? short : long,
  };
};

/** Normalizes version-1 symmetric safe padding and the version-2 model. */
export const normalizeSheetGeometry = (geometry) => {
  const source = geometry || {};
  const paper = resolvePaper(source);
  const columns = toClampedInteger(source.columns, DEFAULT_SHEET_GEOMETRY.columns, SHEET_LIMITS.columns);
  const rows = toClampedInteger(source.rows, DEFAULT_SHEET_GEOMETRY.rows, SHEET_LIMITS.rows);
  const marginX = toFinite(source.safePadXMM, LEGACY_DEFAULT.safePadXMM);
  const marginY = toFinite(source.safePadYMM, LEGACY_DEFAULT.safePadYMM);
  const marginTopMM = toFinite(source.marginTopMM, marginY);
  const marginRightMM = toFinite(source.marginRightMM, marginX);
  const marginBottomMM = toFinite(source.marginBottomMM, marginY);
  const marginLeftMM = toFinite(source.marginLeftMM, marginX);
  const labelColumnGapMM = toFinite(source.labelColumnGapMM, LEGACY_DEFAULT.labelColumnGapMM);
  const labelRowGapMM = toFinite(source.labelRowGapMM, LEGACY_DEFAULT.labelRowGapMM);
  const derivedWidth = (paper.widthMM - marginLeftMM - marginRightMM - (columns - 1) * labelColumnGapMM) / columns;
  const derivedHeight = (paper.heightMM - marginTopMM - marginBottomMM - (rows - 1) * labelRowGapMM) / rows;
  return {
    paperSize: source.paperSize === "custom" ? "custom" : "a4",
    orientation: paper.orientation,
    paperWidthMM: toFinite(source.paperWidthMM, DEFAULT_SHEET_GEOMETRY.paperWidthMM),
    paperHeightMM: toFinite(source.paperHeightMM, DEFAULT_SHEET_GEOMETRY.paperHeightMM),
    columns, rows,
    labelWidthMM: has(source, "labelWidthMM") ? toFinite(source.labelWidthMM, derivedWidth) : derivedWidth,
    labelHeightMM: has(source, "labelHeightMM") ? toFinite(source.labelHeightMM, derivedHeight) : derivedHeight,
    marginTopMM, marginRightMM, marginBottomMM, marginLeftMM,
    // Read-only compatibility aliases for older callers. New persistence never writes them.
    safePadXMM: marginLeftMM === marginRightMM ? marginLeftMM : undefined,
    safePadYMM: marginTopMM === marginBottomMM ? marginTopMM : undefined,
    labelRowGapMM, labelColumnGapMM,
    paddingTopMM: toFinite(source.paddingTopMM, DEFAULT_SHEET_GEOMETRY.paddingTopMM),
    paddingRightMM: toFinite(source.paddingRightMM, DEFAULT_SHEET_GEOMETRY.paddingRightMM),
    paddingBottomMM: toFinite(source.paddingBottomMM, DEFAULT_SHEET_GEOMETRY.paddingBottomMM),
    paddingLeftMM: toFinite(source.paddingLeftMM, DEFAULT_SHEET_GEOMETRY.paddingLeftMM),
  };
};

export const normalizeDeviceCalibration = (calibration) => ({
  printOffsetXMM: toFinite(calibration?.printOffsetXMM, DEFAULT_DEVICE_CALIBRATION.printOffsetXMM),
  printOffsetYMM: toFinite(calibration?.printOffsetYMM, DEFAULT_DEVICE_CALIBRATION.printOffsetYMM),
});

export const computeSheetMetrics = (geometry) => {
  const sheet = normalizeSheetGeometry(geometry);
  const paper = resolvePaper(sheet);
  const gridWidthMM = sheet.columns * sheet.labelWidthMM + (sheet.columns - 1) * sheet.labelColumnGapMM;
  const gridHeightMM = sheet.rows * sheet.labelHeightMM + (sheet.rows - 1) * sheet.labelRowGapMM;
  const availableWidthMM = paper.widthMM - sheet.marginLeftMM - sheet.marginRightMM;
  const availableHeightMM = paper.heightMM - sheet.marginTopMM - sheet.marginBottomMM;
  return {
    paper,
    printableWidthMM: availableWidthMM,
    printableHeightMM: availableHeightMM,
    availableWidthMM,
    availableHeightMM,
    gridWidthMM,
    gridHeightMM,
    labelWidthMM: sheet.labelWidthMM,
    labelHeightMM: sheet.labelHeightMM,
    slotsPerPage: sheet.columns * sheet.rows,
    remainingWidthMM: availableWidthMM - gridWidthMM,
    remainingHeightMM: availableHeightMM - gridHeightMM,
  };
};

/** Issues that make a geometry or calibrated placement physically invalid. */
export const validateSheetGeometry = (geometry, calibration) => {
  const sheet = normalizeSheetGeometry(geometry);
  const offsets = normalizeDeviceCalibration(calibration);
  const metrics = computeSheetMetrics(sheet);
  const issues = [];
  const margins = [sheet.marginTopMM, sheet.marginRightMM, sheet.marginBottomMM, sheet.marginLeftMM];
  const padding = [sheet.paddingTopMM, sheet.paddingRightMM, sheet.paddingBottomMM, sheet.paddingLeftMM];
  if (!(metrics.paper.widthMM >= 50 && metrics.paper.widthMM <= 1000)) issues.push("paper_width_invalid");
  if (!(metrics.paper.heightMM >= 50 && metrics.paper.heightMM <= 1000)) issues.push("paper_height_invalid");
  if (margins.some((value) => value < 0)) issues.push("negative_margin");
  if (padding.some((value) => value < 0)) issues.push("negative_padding");
  if (sheet.labelColumnGapMM < 0 || sheet.labelRowGapMM < 0) issues.push("negative_gap");
  if (!(sheet.labelWidthMM > 0)) issues.push("label_width_not_positive");
  if (!(sheet.labelHeightMM > 0)) issues.push("label_height_not_positive");
  if (sheet.labelWidthMM < 20) issues.push("label_width_too_small");
  if (sheet.labelHeightMM < 15) issues.push("label_height_too_small");
  if (padding[1] + padding[3] >= sheet.labelWidthMM) issues.push("horizontal_padding_overflow");
  if (padding[0] + padding[2] >= sheet.labelHeightMM) issues.push("vertical_padding_overflow");
  if (metrics.gridWidthMM > metrics.availableWidthMM + 0.001) issues.push("grid_width_overflow");
  if (metrics.gridHeightMM > metrics.availableHeightMM + 0.001) issues.push("grid_height_overflow");
  const left = sheet.marginLeftMM + offsets.printOffsetXMM;
  const top = sheet.marginTopMM + offsets.printOffsetYMM;
  if (left < -0.001) issues.push("offset_left_overflow");
  if (top < -0.001) issues.push("offset_top_overflow");
  if (left + metrics.gridWidthMM > metrics.paper.widthMM + 0.001) issues.push("offset_right_overflow");
  if (top + metrics.gridHeightMM > metrics.paper.heightMM + 0.001) issues.push("offset_bottom_overflow");
  return [...new Set(issues)];
};

export const getSheetGeometrySignature = (geometry) => {
  const sheet = normalizeSheetGeometry(geometry);
  return [
    `${sheet.paperSize}:${sheet.orientation}:${sheet.paperWidthMM}x${sheet.paperHeightMM}`,
    `${sheet.columns}x${sheet.rows}`,
    `label${sheet.labelWidthMM}/${sheet.labelHeightMM}`,
    `margin${sheet.marginTopMM}/${sheet.marginRightMM}/${sheet.marginBottomMM}/${sheet.marginLeftMM}`,
    `gap${sheet.labelColumnGapMM}/${sheet.labelRowGapMM}`,
  ].join(":");
};

export const getSlotCoordinates = (position, columns) => ({ row: Math.floor((position - 1) / columns) + 1, column: ((position - 1) % columns) + 1 });
export const getRowStartPositions = (columns, rows) => Array.from({ length: rows }, (_entry, index) => ({ row: index + 1, position: index * columns + 1 }));
export const clampStartPosition = (position, slotsPerPage) => {
  const numeric = Math.round(Number(position));
  if (!Number.isFinite(numeric) || slotsPerPage < 1) return 1;
  return Math.min(slotsPerPage, Math.max(1, numeric));
};
const assertPositiveInteger = (value, name) => {
  if (!Number.isInteger(value) || value < 1) throw new RangeError(`${name} must be a positive integer (received ${value})`);
};

export const planSheetLayout = ({ labelCount, columns, rows, startPosition = 1 }) => {
  assertPositiveInteger(columns, "columns");
  assertPositiveInteger(rows, "rows");
  if (!Number.isInteger(labelCount) || labelCount < 0) throw new RangeError(`labelCount must be a non-negative integer (received ${labelCount})`);
  const slotsPerPage = columns * rows;
  if (!Number.isInteger(startPosition) || startPosition < 1 || startPosition > slotsPerPage) throw new RangeError(`startPosition must be between 1 and ${slotsPerPage} (received ${startPosition})`);
  const pages = [];
  let nextLabelIndex = 0;
  while (nextLabelIndex < labelCount) {
    const firstPosition = pages.length === 0 ? startPosition : 1;
    const capacity = slotsPerPage - firstPosition + 1;
    const pageLabelCount = Math.min(capacity, labelCount - nextLabelIndex);
    const lastPosition = firstPosition + pageLabelCount - 1;
    const firstLabelIndex = nextLabelIndex;
    const slots = Array.from({ length: slotsPerPage }, (_entry, index) => {
      const position = index + 1;
      const { row, column } = getSlotCoordinates(position, columns);
      if (position < firstPosition) return { position, row, column, state: "skipped", labelIndex: null };
      if (position <= lastPosition) return { position, row, column, state: "label", labelIndex: firstLabelIndex + position - firstPosition };
      return { position, row, column, state: "empty", labelIndex: null };
    });
    pages.push({ pageNumber: pages.length + 1, firstPosition, lastPosition, labelCount: pageLabelCount, slots });
    nextLabelIndex += pageLabelCount;
  }
  const lastPage = pages[pages.length - 1] || null;
  const lastPosition = lastPage ? lastPage.lastPosition : null;
  const labelsOnFirstSheet = pages[0] ? pages[0].labelCount : 0;
  return {
    columns, rows, slotsPerPage, startPosition, labelCount, pageCount: pages.length, pages, lastPosition,
    nextFreePosition: lastPosition != null && lastPosition < slotsPerPage ? lastPosition + 1 : null,
    labelsOnFirstSheet,
    labelsAfterFirstSheet: labelCount - labelsOnFirstSheet,
  };
};
